import { type Member, actorWords } from './people'

/**
 * The field **Correct…** opens on, for a Raw Data row.
 *
 * The field the row is held for comes first: a GSTIN that fails its checksum, then a place of
 * supply GSTR-1 holds the sale for (D-144) — the hold that had no control at all until N24
 * (2026-10-22) — then an invoice type GSTR-1 holds it for (D-148). An export is offered its
 * shipping bill; anything else, its unit.
 */
export function correctionFieldFor(row: {
  failing: string[]
  is_export?: boolean
  needs_place_of_supply?: boolean
  invoice_type_hold?: { code: string } | null
}): string {
  if (row.failing.includes('gstin_checksum')) return 'gstin_buyer'
  if (row.needs_place_of_supply) return 'place_of_supply'
  if (row.invoice_type_hold) return 'invoice_type'
  if (row.is_export) return 'shipping_bill_no'
  return 'uqc'
}

/**
 * What **Take it out**'s "Why?" offers (N34): the exclusion reasons the service serves and
 * validates, in its order. It offered the money-correction reasons, none of which is a reason to
 * remove a row.
 */
export function excludeChoices(body: { exclude_reasons: Record<string, string> }): [string, string][] {
  return Object.entries(body.exclude_reasons)
}

/**
 * Whether **Correct…** is offered on a row: it is held, an export, or GSTR-1 holds it for its
 * place of supply (D-144) or its invoice type (D-148).
 */
export function offersCorrection(row: {
  accepted: boolean
  is_export?: boolean
  needs_place_of_supply?: boolean
  invoice_type_hold?: { code: string } | null
}): boolean {
  return (
    !row.accepted ||
    Boolean(row.is_export) ||
    Boolean(row.needs_place_of_supply) ||
    Boolean(row.invoice_type_hold)
  )
}

/**
 * D-151 (W3-16 (d) part 2a): **Convert at a rate…** is offered on a sale GSTR-1 holds for its
 * rupees (`needs_inr_value`) — one stating a foreign currency with no value in that currency
 * beside its rupees — and never on one already converted, taken out, or whose register gives its
 * rupees (D-149), which the service does not flag.
 */
export function offersConversion(row: {
  needs_inr_value?: boolean
  converted_by?: unknown
  excluded_by?: unknown
}): boolean {
  return Boolean(row.needs_inr_value) && !row.converted_by && !row.excluded_by
}

/** The reasons a typed rate may give, as the service serves and validates them (G21 = A). */
export function rateChoices(body: { rate_reasons?: Record<string, string> }): [string, string][] {
  return Object.entries(body.rate_reasons ?? {})
}

/** What the rate box asks for: rupees for ONE unit of the invoice's currency. */
export function rateLabel(currency: unknown): string {
  const code = currency === null || currency === undefined ? '' : String(currency).trim()
  return `₹ for 1 ${code || 'unit of the currency'}`
}

export interface ConvertedBy {
  currency: string
  rate: string
  reason_code?: string | null
  note?: string | null
  entered_by?: string | null
  by?: string | null
}

/** The invoice's value in its own currency, as read: its invoice value, else its taxable value. */
function foreignAmount(effective: Record<string, unknown>): string | null {
  const conversion = effective.inr_conversion as { foreign?: Record<string, unknown> } | undefined
  const foreign = conversion?.foreign ?? {}
  const cents = typeof foreign.invoice_value === 'number' ? foreign.invoice_value : foreign.taxable
  if (typeof cents !== 'number') return null
  return (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * "USD 1,000.00 at ₹83.2150 — rate entered by Asha (The customs rate (shipping bill): …)" — what
 * a converted row says, with `who` resolved by the caller (`people.actorWords`).
 */
export function conversionWords(
  converted: ConvertedBy,
  effective: Record<string, unknown>,
  reasons: Record<string, string>,
  who: string,
): string {
  const amount = foreignAmount(effective)
  const label = converted.reason_code ? reasons[converted.reason_code] ?? converted.reason_code : null
  const note = converted.note?.trim()
  const why = [label, note].filter(Boolean).join(': ')
  return (
    `${converted.currency}${amount ? ` ${amount}` : ''} at ₹${converted.rate} — rate entered by ${who}` +
    (why ? ` (${why})` : '')
  )
}

/**
 * Who typed the rate, in words: the platform's member list through `actorWords` ("you", a
 * name), and — where that list cannot say — the name or email kept when the rate was typed.
 */
export function rateTypedBy(
  converted: Pick<ConvertedBy, 'by' | 'entered_by'>,
  members: readonly Member[] | null,
  me: string | null,
): string {
  const who = actorWords({ actor_kind: 'user', actor_user_id: converted.by ?? null }, members, me)
  const kept = converted.entered_by?.trim()
  return kept && (who === 'a member of this firm' || who === 'someone no longer in this firm')
    ? kept
    : who
}
